import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { fetchCart, markCartChanged, onKrakenCartChange, publishCartCount } from '@theme/kraken-cart-bridge';
import bundleConfig, { mainLineProperty } from '@theme/bundle-gift-config';

/**
 * Reconciles Kaching Bundles' progressive free gift against what is actually in the cart.
 *
 * Two independent defects make this necessary, both verified in the browser.
 *
 * 1. Non-PDP add-to-cart writes bundle-blind lines.
 *    `kaching-bundles.js → CartInterceptor._findBundleItems()` returns null unless the
 *    `/cart/add.js` body already carries a `__kaching_bundles` property, or a deal was clicked in a
 *    widget within the last 2s, or a `<kaching-bundle>` widget is registered on the page. On
 *    collection/search/upsell pages there are zero `<kaching-bundle>` nodes and every card is
 *    single-variant (Horizon adds directly, never opening the quick-add modal), so lines land with
 *    `properties: {}`.
 *
 * 2. Quantity changes in the drawer are never reconciled.
 *    Kraken Cart mutates through the Storefront API, so Kaching's `_maybeRewriteCartChangeResponse`
 *    hook never fires, and `kaching-bundles.js` has no `/cart/change` or `/cart/update` calls at all.
 *
 * The Kaching discount function splits along the same seam: the volume tier needs only product +
 * quantity so it always applies, while the gift needs the line properties so it silently does not.
 * That is why the cart shows DUO pricing but no free CHARGE+ — and, going the other way, why
 * lowering 2 → 1 leaves a "GRATIS" line behind that is then billed at full price.
 *
 * The fix is to own the line-item layer: key deals on `product_id` (the one thing every add path
 * gets right), stamp the main lines with the property the discount function needs, and add or remove
 * the gift line to match. Line shape proven to work via `/cart/change.js`:
 *
 *   main line: { deal, pp: <productId>, main: true, id: <groupId>, bid: <dealBarUuid> }
 *   gift line: { id: <same groupId>, deal, gift: <giftId> }
 *
 * `bid` does not drive pricing — the function re-derives the tier from the actual line quantity — so
 * a stale `bid` is never restamped.
 *
 * Safety rules:
 *   - Only lines whose `__kaching_bundles` property carries a `gift` key are ever deleted. A
 *     customer-purchased CHARGE+ has `properties: {}` and is never touched.
 *   - Idempotent: a second pass over a correct cart performs zero mutations.
 *   - Bounded: consecutive mutating passes are capped, so our own writes cannot loop.
 *
 * @typedef {object} BundleLineInfo
 * @property {string} [id] - Group id linking a gift line to its main line(s).
 * @property {string} [deal] - Kaching deal id.
 * @property {boolean} [main] - True on the main product line.
 * @property {string} [gift] - Gift id; present only on gift lines.
 *
 * @typedef {object} DealState
 * @property {string} dealId
 * @property {Record<string, any>} deal
 * @property {string} groupId
 * @property {number} mainQty
 * @property {Record<string, any>[]} mainLines
 * @property {Map<string, Record<string, any>[]>} giftLines - Keyed by gift id.
 */

const BUNDLE_PROPERTY = '__kaching_bundles';
const DEBOUNCE_MS = 250;
const MAX_MUTATING_PASSES = 3;
const IDLE_RESET_MS = 5000;
const MAX_ADD_FAILURES = 2;
const NOTICE_TIMEOUT_MS = 6000;
const GROUP_ID_LENGTH = 4;
const GROUP_ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const DISMISSED_STORAGE_KEY = 'bundle-gift-dismissed';

/** @type {Map<string, { giftQty: number, mainQty: number }>} Observed state from the previous pass. */
let previousState = new Map();

/** @type {Map<string, number>} Gift id → failed add attempts this session. */
const addFailures = new Map();

/** @type {Set<string>} Pair keys this session added itself, used by the billed-gift guard. */
const sessionAddedPairs = new Set();

let isReconciling = false;
let rerunRequested = false;
let mutatingPasses = 0;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let debounceTimer;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let idleTimer;

/**
 * @param {Record<string, any>} line
 * @returns {BundleLineInfo | null}
 */
function readBundleInfo(line) {
  const raw = line.properties?.[BUNDLE_PROPERTY];

  if (!raw) return null;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Resolves the main-product quantity at which a gift unlocks.
 *
 * `unlockAtBar` is matched against the bars' `quantity` first, then treated as a 1-based bar index.
 * Both readings agree for the current Gamechanger config (bars 1/2/3, `unlockAtBar: 2`); they stop
 * agreeing if a merchant inserts a bar, so a divergence is logged rather than silently picked.
 *
 * @param {Record<string, any>} deal
 * @param {Record<string, any>} gift
 * @returns {number}
 */
function resolveThreshold(deal, gift) {
  const bars = Array.isArray(deal.dealBars) ? deal.dealBars : [];
  const target = Number(gift.unlockAtBar);
  const byQuantity = bars.find((bar) => bar.quantity === target)?.quantity;
  const byIndex = bars[target - 1]?.quantity;

  if (typeof byQuantity === 'number' && typeof byIndex === 'number' && byQuantity !== byIndex) {
    console.warn(
      `[bundle-gift-reconciler] gift "${gift.id}": unlockAtBar ${target} resolves to ${byQuantity} by bar ` +
        `quantity but ${byIndex} by bar index — using ${byQuantity}. Verify against the Kaching admin.`
    );
  }

  return byQuantity ?? byIndex ?? target;
}

/** @returns {string} A group id in the same shape Kaching generates (e.g. "tVPN"). */
function generateGroupId() {
  return Array.from(
    { length: GROUP_ID_LENGTH },
    () => GROUP_ID_ALPHABET[Math.floor(Math.random() * GROUP_ID_ALPHABET.length)]
  ).join('');
}

/**
 * Buckets the cart's lines per configured deal.
 *
 * Main lines are found by `product_id`, the one thing every add path gets right. Gift lines are
 * found by their property, the only thing that distinguishes our gift from a CHARGE+ the customer
 * chose to buy.
 *
 * @param {Record<string, any>} cart
 * @returns {DealState[]}
 */
function collectDeals(cart) {
  const lines = cart.items ?? [];

  return Object.entries(bundleConfig.deals ?? {})
    .map(([dealId, deal]) => {
      /** @type {DealState} */
      const state = { dealId, deal, groupId: '', mainQty: 0, mainLines: [], giftLines: new Map() };
      const giftIds = new Set((deal.gifts ?? []).map((gift) => gift.id));

      for (const line of lines) {
        const info = readBundleInfo(line);

        if (info?.gift) {
          if (info.deal !== dealId || !giftIds.has(info.gift)) continue;

          state.giftLines.set(info.gift, [...(state.giftLines.get(info.gift) ?? []), line]);
          state.groupId ||= info.id ?? '';

          continue;
        }

        if (line.product_id !== deal.productId) continue;

        state.mainLines.push(line);
        state.mainQty += line.quantity;
        state.groupId ||= info?.id ?? '';
      }

      state.groupId ||= generateGroupId();

      return state;
    })
    .filter((state) => state.mainLines.length > 0 || state.giftLines.size > 0);
}

/**
 * A main line only needs restamping when the discount function would not recognise it. `bid` is
 * deliberately excluded — it does not drive pricing, so restamping on every tier change is churn.
 *
 * @param {Record<string, any>} line
 * @param {DealState} state
 * @returns {boolean}
 */
function needsStamp(line, state) {
  const info = readBundleInfo(line);

  return !(
    info?.main === true &&
    info.deal === state.dealId &&
    info.pp === state.deal.productId &&
    info.id === state.groupId
  );
}

/** @returns {Set<string>} */
function readDismissed() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_STORAGE_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

/** @param {Set<string>} dismissed */
function writeDismissed(dismissed) {
  try {
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed]));
  } catch {
    // Non-fatal: the worst case is a dismissed gift returning on the next cart change.
  }
}

/**
 * Plans every change without touching the cart.
 *
 * @param {DealState[]} states
 * @returns {{ actions: any[], nextState: Map<string, any>, anyUnlocked: boolean }}
 */
function planActions(states) {
  const actions = [];
  const nextState = new Map();
  const dismissed = readDismissed();
  const dismissedBefore = JSON.stringify([...dismissed]);
  let anyUnlocked = false;

  for (const state of states) {
    const { deal, dealId } = state;

    if (deal.progressiveGiftsEnabled === false) continue;

    for (const gift of deal.gifts ?? []) {
      const threshold = resolveThreshold(deal, gift);

      if (!Number.isFinite(threshold)) continue;

      const pairKey = `${dealId}:${gift.id}`;
      const lines = state.giftLines.get(gift.id) ?? [];
      const actualQty = lines.reduce((sum, line) => sum + line.quantity, 0);
      const unlocked = state.mainQty >= threshold;
      const desiredQty = unlocked ? Math.max(1, Number(gift.quantity) || 1) : 0;
      const previous = previousState.get(pairKey);

      nextState.set(pairKey, { giftQty: actualQty, mainQty: state.mainQty });
      anyUnlocked ||= unlocked;

      // The customer deleted the gift while it was still unlocked → respect that for this session.
      if (unlocked && actualQty === 0 && previous && previous.giftQty > 0 && previous.mainQty >= threshold) {
        dismissed.add(pairKey);
      }

      // Dropping below the threshold resets the dismissal, so re-crossing it grants the gift again.
      if (!unlocked) dismissed.delete(pairKey);

      if (!unlocked) {
        for (const line of lines) {
          actions.push({
            type: 'change',
            pairKey,
            lineKey: line.key,
            quantity: 0,
            previousQuantity: line.quantity,
            productTitle: line.product_title,
            reason: 'locked',
            gift,
            state,
            threshold,
          });
        }

        continue;
      }

      if (dismissed.has(pairKey)) continue;

      // Only stamp once the gift is actually due: below the threshold, or when the customer has
      // dismissed the gift, the volume tier applies on product + quantity alone, so stamping would
      // be a mutation with no effect.
      // One stamp per pass — changing a line's properties changes its key, and merging two main
      // lines into one would invalidate any further key planned against this cart snapshot.
      const stampable = state.mainLines.find((line) => needsStamp(line, state));

      if (stampable && !actions.some((action) => action.type === 'stamp')) {
        actions.push({
          type: 'stamp',
          pairKey,
          lineKey: stampable.key,
          quantity: stampable.quantity,
          properties: {
            [BUNDLE_PROPERTY]: mainLineProperty({ dealId, deal, groupId: state.groupId, quantity: state.mainQty }),
          },
          reason: 'stamp-main',
          gift,
          state,
          threshold,
        });
      }

      // Wait for the main line to be stamped before adding the gift: a gift line whose group id no
      // main line carries would still not trigger the discount function.
      if (stampable) continue;

      if (actualQty === 0) {
        actions.push({ type: 'add', pairKey, quantity: desiredQty, reason: 'unlocked', gift, state, threshold });

        continue;
      }

      // Keep one gift line and drop the rest: Kaching and Kraken both intercept `/cart/add.js`, so a
      // duplicate is a real possibility.
      const [keep, ...extra] = lines;

      for (const line of extra) {
        actions.push({
          type: 'change',
          pairKey,
          lineKey: line.key,
          quantity: 0,
          previousQuantity: line.quantity,
          productTitle: line.product_title,
          reason: 'duplicate',
          gift,
          state,
          threshold,
        });
      }

      // A gift we added that is not actually free must never reach checkout. Only our own additions
      // are policed; Kaching's own lines are left to the app.
      if (sessionAddedPairs.has(pairKey) && keep.final_line_price > 0) {
        console.warn(
          `[bundle-gift-reconciler] gift "${gift.id}" is billed at ${keep.final_line_price} — the Kaching ` +
            'discount function did not zero it. Removing it again.'
        );

        dismissed.add(pairKey);
        actions.push({
          type: 'change',
          pairKey,
          lineKey: keep.key,
          quantity: 0,
          previousQuantity: keep.quantity,
          productTitle: keep.product_title,
          reason: 'billed',
          gift,
          state,
          threshold,
        });

        continue;
      }

      if (keep.quantity !== desiredQty) {
        actions.push({
          type: 'change',
          pairKey,
          lineKey: keep.key,
          quantity: desiredQty,
          previousQuantity: keep.quantity,
          productTitle: keep.product_title,
          reason: 'normalise',
          gift,
          state,
          threshold,
        });
      }
    }
  }

  for (const key of dismissed) {
    if (!nextState.has(key)) dismissed.delete(key);
  }

  if (JSON.stringify([...dismissed]) !== dismissedBefore) writeDismissed(dismissed);

  return { actions, nextState, anyUnlocked };
}

/**
 * @param {DealState} state
 * @param {Record<string, any>} gift
 * @param {number} quantity
 * @returns {Promise<string>} The added product's title, for the customer-facing notice.
 */
async function addGiftLine(state, gift, quantity) {
  const body = JSON.stringify({
    items: [
      {
        id: gift.variantId,
        quantity,
        properties: {
          [BUNDLE_PROPERTY]: JSON.stringify({ id: state.groupId, deal: state.dealId, gift: gift.id }),
        },
      },
    ],
  });

  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));

  if (!response.ok) throw new Error(`cart/add returned ${response.status}`);

  const added = await response.json();

  return added?.items?.[0]?.product_title ?? '';
}

/**
 * @param {string} lineKey
 * @param {number} quantity
 * @param {Record<string, string>} [properties]
 */
async function changeLine(lineKey, quantity, properties) {
  const body = JSON.stringify(properties ? { id: lineKey, quantity, properties } : { id: lineKey, quantity });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));

  if (!response.ok) throw new Error(`cart/change returned ${response.status}`);
}

/**
 * @param {any[]} actions
 * @returns {Promise<any[]>} The actions that actually landed.
 */
async function applyActions(actions) {
  const executed = [];

  // Sequential on purpose: the cart is a single shared resource and parallel writes race.
  for (const action of actions) {
    // Sampled right before each write, not once per pass: a pass spans several round trips, and a
    // drawer the customer opened in the meantime must not be mistaken for one Kraken popped open.
    const drawerOpenBefore = safeIsDrawerOpen();

    try {
      if (action.type === 'add') {
        if ((addFailures.get(action.gift.id) ?? 0) >= MAX_ADD_FAILURES) continue;

        if (!action.gift.variantId) {
          addFailures.set(action.gift.id, MAX_ADD_FAILURES);
          console.warn(
            `[bundle-gift-reconciler] gift "${action.gift.id}" has no variantId in assets/bundle-gift-config.js.`
          );

          continue;
        }

        action.productTitle = await addGiftLine(action.state, action.gift, action.quantity);
        sessionAddedPairs.add(action.pairKey);
      } else {
        await changeLine(action.lineKey, action.quantity, action.properties);
      }

      action.openedDrawer = !drawerOpenBefore && safeIsDrawerOpen();
      executed.push(action);
    } catch (error) {
      if (action.type === 'add') addFailures.set(action.gift.id, (addFailures.get(action.gift.id) ?? 0) + 1);

      // Never block the cart over a gift: out of stock, unpublished and sold out all land here.
      console.warn(`[bundle-gift-reconciler] ${action.type} (${action.reason}) failed`, error);
    }
  }

  return executed;
}

async function reconcile() {
  if (isReconciling) {
    rerunRequested = true;

    return;
  }

  isReconciling = true;

  try {
    await runPass();
  } finally {
    isReconciling = false;

    if (rerunRequested) {
      rerunRequested = false;
      schedule();
    }
  }
}

/** One reconciliation pass: read the cart, plan, mutate, repaint. */
async function runPass() {
  // `schedule()` debounces, so a snapshot taken at event time could be stale — but the bridge's
  // event handler has usually fetched the cart after the change already, and that request is reused.
  const cart = (await fetchCart({ sinceLastChange: true })) ?? (await fetchCart({ fresh: true }));

  if (!cart) return;

  const states = collectDeals(cart);

  if (!states.length) {
    previousState = new Map();
    mutatingPasses = 0;
    // No bundle in the cart means no dismissal to honour: a gift the customer removed and then
    // re-earned by adding the bundle again from scratch must be granted again. `planActions()`
    // normally prunes this, but it never runs on an empty state.
    if (readDismissed().size) writeDismissed(new Set());
    syncBundleWidget(false);

    return;
  }

  const { actions, nextState, anyUnlocked } = planActions(states);

  syncBundleWidget(anyUnlocked);

  if (!actions.length) {
    previousState = nextState;
    mutatingPasses = 0;

    return;
  }

  if (mutatingPasses >= MAX_MUTATING_PASSES) {
    console.warn(
      `[bundle-gift-reconciler] stopping after ${MAX_MUTATING_PASSES} consecutive mutating passes — ` +
        'the cart is not converging.'
    );
    previousState = nextState;

    return;
  }

  const executed = await applyActions(actions);

  // Record the state we expect, so the next pass does not read our own removal as a customer
  // dismissal.
  for (const action of executed) {
    const entry = nextState.get(action.pairKey);

    if (!entry || action.type === 'stamp') continue;

    entry.giftQty =
      action.type === 'add'
        ? entry.giftQty + action.quantity
        : Math.max(0, entry.giftQty - (action.previousQuantity - action.quantity));
  }

  previousState = nextState;

  if (!executed.length) {
    mutatingPasses = 0;

    return;
  }

  mutatingPasses += 1;
  scheduleIdleReset();
  announce(executed);

  try {
    await window.KrakenCart?.updateCart?.();
  } catch (error) {
    console.warn('[bundle-gift-reconciler] KrakenCart.updateCart() failed', error);
  }

  // Our `/cart/add.js` call can make Kraken pop the drawer open. Undo that, but only when one of our
  // own writes flipped it — never a drawer the customer opened while the pass was running.
  if (executed.some((action) => action.openedDrawer) && safeIsDrawerOpen()) {
    try {
      window.KrakenCart?.toggleCart?.(false);
    } catch {
      // Non-fatal.
    }
  }

  const updated = await fetchCart({ fresh: true });

  if (updated) publishCartCount(updated.item_count);

  // A stamp is only ever step one — the gift add waits for it, so run the follow-up pass here rather
  // than relying on Kraken to emit an event for our own write.
  if (executed.some((action) => action.type === 'stamp')) schedule();
}

/** @returns {boolean} */
function safeIsDrawerOpen() {
  try {
    return window.KrakenCart?.isActive?.() === true;
  } catch {
    return false;
  }
}

function scheduleIdleReset() {
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    mutatingPasses = 0;
  }, IDLE_RESET_MS);
}

function schedule() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    reconcile().catch((error) => {
      console.warn('[bundle-gift-reconciler] reconciliation failed', error);
    });
  }, DEBOUNCE_MS);
}

/**
 * @param {string} template
 * @param {Record<string, string | number>} values
 */
function interpolate(template, values) {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`[${key}]`, String(value)),
    template ?? ''
  );
}

/**
 * Product names come from the cart line / add response rather than the config mirror, so the notice
 * always shows the real, translatable product title.
 *
 * @param {any[]} executed
 */
function announce(executed) {
  const added = executed.find((action) => action.type === 'add');
  const removed = executed.find((action) => action.type === 'change' && action.reason === 'locked');
  const translations = (typeof Theme !== 'undefined' && Theme.translations) || {};

  if (added?.productTitle) {
    showNotice(interpolate(translations.bundle_gift_added, { gift: added.productTitle }));
  }

  // No main line left means the customer deleted the whole bundle — a "you can win it back" notice
  // would just be noise there.
  if (removed?.productTitle && removed.state.mainLines.length) {
    showNotice(
      interpolate(translations.bundle_gift_removed, {
        gift: removed.productTitle,
        quantity: removed.threshold,
        product: removed.state.mainLines[0].product_title ?? '',
      })
    );
  }
}

/** @param {string} message */
function showNotice(message) {
  if (!message) return;

  const container =
    document.querySelector('.bundle-gift-notices') ??
    document.body.appendChild(Object.assign(document.createElement('div'), { className: 'bundle-gift-notices' }));

  const notice = document.createElement('div');

  notice.className = 'bundle-gift-notice';
  notice.setAttribute('role', 'status');
  notice.textContent = message;
  container.appendChild(notice);

  setTimeout(() => notice.remove(), NOTICE_TIMEOUT_MS);
}

/**
 * Keeps the PDP `<kaching-bundle>` widget from contradicting the drawer — it renders its
 * locked/unlocked gift row once at page load and never re-reads the cart.
 *
 * The app exposes no documented refresh entry point, and the undocumented ones are not safe to
 * call: `update()`/`render()` are Lit lifecycle internals, and `KachingBundles.init()` re-wraps
 * `window.fetch`, which would double-rewrite every later `/cart/add.js` body. So this only sets
 * `data-bundle-gift-state` — a stable CSS hook we own — and announces the change as an event.
 *
 * @param {boolean} unlocked
 */
function syncBundleWidget(unlocked) {
  const widget = document.querySelector('kaching-bundle');

  if (!widget) return;

  const state = unlocked ? 'unlocked' : 'locked';

  if (widget.getAttribute('data-bundle-gift-state') === state) return;

  widget.setAttribute('data-bundle-gift-state', state);
  widget.dispatchEvent(new CustomEvent('kaching:refresh', { bubbles: true, detail: { unlocked } }));
}

onKrakenCartChange(schedule);

// On the /cart page quantity changes go through the theme's own `component-cart-items`, which uses
// the AJAX cart routes and emits `cart:update` rather than a Kraken event. Ignore our own
// re-publishes so this cannot feed back into itself.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (/** @type {CartUpdateEvent} */ (event).detail?.data?.source === 'kraken-cart') return;

  markCartChanged();
  schedule();
});
